import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

// Hook daftar server-side: pagination + search (debounce) + filter + sort, race-safe.
// endpoint: string; filters: object of extra query params; returns items/total/page/pages.
export function useServerList(endpoint, { initialLimit = 25, filters = {}, sort = "", dir = "asc", debounceMs = 400 } = {}) {
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(initialLimit);
  const [sortState, setSortState] = useState({ sort, dir });
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const seq = useRef(0);
  const filtersKey = JSON.stringify(filters);

  // Debounce pencarian.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), debounceMs);
    return () => clearTimeout(t);
  }, [q, debounceMs]);

  // Reset ke halaman 1 saat search/filter/sort/limit berubah.
  useEffect(() => { setPage(1); }, [debouncedQ, filtersKey, limit, sortState.sort, sortState.dir]);

  const fetchList = useCallback(() => {
    const mySeq = ++seq.current;
    setLoading(true);
    setError(false);
    const params = { page, limit, q: debouncedQ || undefined, ...filters };
    if (sortState.sort) { params.sort = sortState.sort; params.dir = sortState.dir; }
    api.get(endpoint, { params })
      .then((r) => {
        if (mySeq !== seq.current) return; // buang respons usang
        const d = r.data;
        setData(Array.isArray(d) ? { items: d, total: d.length, page: 1, pages: 1 } : d);
      })
      .catch(() => { if (mySeq === seq.current) setError(true); })
      .finally(() => { if (mySeq === seq.current) setLoading(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, page, limit, debouncedQ, filtersKey, sortState.sort, sortState.dir]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const toggleSort = useCallback((key) => {
    setSortState((s) => s.sort === key ? { sort: key, dir: s.dir === "asc" ? "desc" : "asc" } : { sort: key, dir: "asc" });
  }, []);

  return {
    q, setQ, page, setPage, limit, setLimit, sort: sortState, toggleSort,
    items: data.items || [], total: data.total || 0, pages: data.pages || 1,
    loading, error, reload: fetchList,
  };
}
